import { localizedStrings } from '@core/strings'
import React from 'react'
import { Box } from '@fundamentals'
import { Button, IconButton, Tooltip } from '@common/material'
import {
  LinkRegisterItemsQuestionRules,
  QuestionTypeComponentType,
} from '@core/components/templateAnswersForm'
import { useFormContext } from 'react-hook-form'
import { RegisterItemCard, SelectRegisterItemsModal } from '@elements'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import { Collapse } from '@mui/material'
import { TransitionGroup } from 'react-transition-group'
import { PageUrls } from '@core/page-urls'
import LaunchIcon from '@mui/icons-material/Launch'
import { useRouter } from 'next/router'
import { useNavigationContext } from '@common/hooks'

const LinkRegisterItemsQuestion: QuestionTypeComponentType = ({
  question,
  inputProps: { value = question, onChange } = {},
  readOnly,
}) => {
  const router = useRouter()
  const { organisationId, projectId: urlProjectId } = useNavigationContext()
  const formContext = useFormContext()
  const projectId = formContext?.getValues('projectId')
  const pinnedFilters = question?.pinnedFilters || []
  const previewPropertyIds =
    question?.previewProperties?.map((property) => property.id) || []

  const handleChangeSelectedRegisterItems = (
    registerItems: Array<RegisterItem>,
  ) => {
    onChange({
      ...value,
      registerItemIds: registerItems.map(({ id }) => id),
      registerItems: registerItems.map((registerItem) =>
        registerItem.toPlain(),
      ),
    })
  }

  const handleRemoveSelectedRegisterItem = (registerItemId: number) => {
    onChange({
      ...value,
      registerItems: value.registerItems.filter(
        (registerItem) => registerItem.id !== registerItemId,
      ),
      registerItemIds: value.registerItemIds.filter(
        (id) => id !== registerItemId,
      ),
    })
  }

  return (
    <Box data-test={`${question.id}-LINK_REGISTER_ITEMS`}>
      <TransitionGroup>
        {value?.registerItems?.map((registerItem, index) => {
          return (
            <Collapse key={index}>
              <RegisterItemCard
                registerItem={RegisterItem.new(registerItem)}
                previewPropertyIds={previewPropertyIds}
                icon={
                  !readOnly ? (
                    question.preSelected ? null : (
                      <IconButton
                        color='error'
                        onClick={() =>
                          handleRemoveSelectedRegisterItem(registerItem.id)
                        }
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    )
                  ) : (
                    !!registerItem.register && (
                      <IconButton
                        color='primary'
                        size='small'
                        onClick={() => {
                          router.push(
                            PageUrls.projectRegisterItemDetails({
                              organisationId,
                              projectId: urlProjectId,
                              registerId: registerItem.register.id,
                              registerItemId: registerItem.id,
                            }),
                          )
                        }}
                      >
                        <Tooltip title={localizedStrings.viewRegisterItem}>
                          <LaunchIcon fontSize='small' />
                        </Tooltip>
                      </IconButton>
                    )
                  )
                }
              />
            </Collapse>
          )
        })}
      </TransitionGroup>
      {!readOnly && (
        <SelectRegisterItemsModal
          organisationId={organisationId}
          projectId={projectId}
          registerTemplateId={question.linkedRegisterTemplate?.id}
          allowMultipleSelect={question.allowMultipleLinkedRegisterItems}
          value={value.registerItems?.map((registerItem) =>
            RegisterItem.new(registerItem),
          )}
          onChange={handleChangeSelectedRegisterItems}
          previewPropertyIds={previewPropertyIds}
          pinnedFilterIds={pinnedFilters.map(({ id }) => id)}
          triggerComponent={(onClick) =>
            question.preSelected ? null : (
              <Button variant='contained' onClick={onClick} sx={{ mt: 1 }}>
                {localizedStrings.selectRegisterItem}
              </Button>
            )
          }
        />
      )}
    </Box>
  )
}

LinkRegisterItemsQuestion.rules = LinkRegisterItemsQuestionRules

export { LinkRegisterItemsQuestion }
